import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable, throwError } from 'rxjs'

import {
  MediaAppInfo,
  MediaLibraryResource,
} from '@flowx/designer/modules/core/dictionary/media-library.dictionary'
import { PaginatedResponse } from '@flowx/designer/shared/services/paginated-datasource'

import { ApiHttpService, PaginationQueryParams } from './api-http.service'

export interface MediaLibraryManager {
  getMediaLibraryResources<T>(
    params: PaginationQueryParams,
    allImages?: boolean
  ):
    | Observable<PaginatedResponse<T>> // media-library.api.ts
    | Observable<{
        resourceContent: T[]
        appsInfo?: MediaAppInfo
      }> // media-library-nominal.api.ts
  addFile(newFile: FormData): Observable<MediaLibraryResource>

  updateImage(imageId: string, newImage: FormData): Observable<MediaLibraryResource>

  deleteImage(id: string): Observable<void>

  exportMediaLibraryResources(params: any): Observable<PaginatedResponse<MediaLibraryResource>>

  exportMediaLibraryResourcesWithBinaries(params: any): Observable<Blob>

  importImages(
    payload: { images: MediaLibraryResource[] },
    withBinaries: boolean
  ): Observable<MediaLibraryResource[]>

  checkImageVersion(imageId: string): Observable<any>
}

@Injectable({ providedIn: 'root' })
export class MediaLibraryApi extends ApiHttpService implements MediaLibraryManager {
  private url = 'cms/api/media-library/images'

  constructor() {
    const baseUrl = inject<string>('BASE_URL' as any)
    const httpClient = inject(HttpClient)

    super(baseUrl, httpClient)
  }

  getMediaLibraryResources<T>(params: PaginationQueryParams): Observable<PaginatedResponse<T>> {
    return this.get(`${this.url}`, { params: this.addParams(params) })
  }

  addFile(newFile: FormData): Observable<MediaLibraryResource> {
    return this.post(`${this.url}`, newFile)
  }

  updateImage(imageId: string, newImage: FormData): Observable<MediaLibraryResource> {
    return this.put(`${this.url}/${imageId}`, newImage)
  }

  deleteImage(id: string): Observable<void> {
    return this.delete(`${this.url}/${id}`, { params: { force: true } })
  }

  exportMediaLibraryResources(params: any): Observable<PaginatedResponse<MediaLibraryResource>> {
    return this.get(`${this.url}/export`, { params: this.addParams(params) })
  }

  exportMediaLibraryResourcesWithBinaries(params: any): Observable<Blob> {
    return this.get(`${this.url}/export-with-binaries`, {
      params: this.addParams(params),
      responseType: 'blob',
    })
  }

  importImages<T>(payload: T, withBinaries: boolean = false): Observable<any> {
    const endpoint = withBinaries ? `${this.url}/import-with-binaries` : `${this.url}/import`
    return this.post(endpoint, payload)
  }

  checkImageVersion(imageId: string): Observable<any> {
    return throwError(() => new Error('Method not implemented', { cause: { imageId } }))
  }
}
